<!-- ----------------------------------------- template ----------------------------------------- -->
<template>
  <div class="club-profile">
    <div class="profile-section">
      <div class="profile-header">
        <h2>Club Avatar</h2>
        <el-button 
          v-if="canEdit && !isEditingAvatar"
          type="primary" 
          size="small"
          @click="handleEditAvatar"
        >
          Edit
        </el-button>
      </div>
      
      <div class="profile-content">
        <template v-if="!isEditingAvatar">
          <el-avatar 
            :size="100" 
            :src="clubInfo.avatar || ''"
            class="club-avatar"
            :style="clubAvatarStyle()"
          >
            {{ clubInfo.name ? clubInfo.name.charAt(0).toUpperCase() : 'C' }}
          </el-avatar>
        </template>
        <div v-else>
          <el-upload
            class="avatar-uploader"
            :action="uploadUrl"
            :show-file-list="false"
            :on-success="handleAvatarSuccess"
            :before-upload="beforeAvatarUpload"
          >
            <img v-if="tempAvatarUrl" :src="tempAvatarUrl" class="avatar-preview">
            <img v-else-if="editedInfo.avatar" :src="editedInfo.avatar" class="avatar-preview">
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          
          <div class="action-buttons">
            <el-button type="primary" size="small" @click="handleSaveAvatar">Save</el-button>
            <el-button size="small" @click="handleCancelAvatar">Cancel</el-button>
          </div>
        </div>
      </div>
    </div>

    <div class="profile-section">
      <div class="profile-header">
        <h2>Club Name</h2>
        <el-button 
          v-if="canEdit && !isEditingName"
          type="primary" 
          size="small"
          @click="handleEditName"
        >
          Edit
        </el-button>
      </div>
      
      <div class="profile-content">
        <template v-if="!isEditingName">
          <p>
            {{ clubInfo.name || 'No name available' }}
            <span v-if="clubInfo.account_nameEN || clubInfo.account_name" class="school-name-display">
              @ {{ clubInfo.account_nameEN || clubInfo.account_name }}
            </span>
          </p>
        </template>
        <el-input
          v-else
          v-model="editedInfo.name"
          placeholder="Enter club name"
        />

        <div v-if="isEditingName" class="action-buttons">
          <el-button type="primary" size="small" @click="handleSaveName">Save</el-button>
          <el-button size="small" @click="handleCancelName">Cancel</el-button>
        </div>
      </div>
    </div>

    <div class="profile-section">
      <div class="profile-header">
        <h2>Club Introduction</h2>
        <el-button 
          v-if="canEdit && !isEditing"
          type="primary" 
          size="small"
          @click="handleEdit"
        >
          Edit
        </el-button>
      </div>
      
      <div class="profile-content">
        <template v-if="!isEditing">
          <p v-if="clubInfo.intro">{{ clubInfo.intro }}</p>
          <p v-else class="no-content">No introduction available</p>
        </template>
        <el-input
          v-else
          type="textarea"
          v-model="editedInfo.intro"
          :rows="4"
          placeholder="Enter club introduction"
        />
        
        <div v-if="isEditing" class="action-buttons">
          <el-button type="primary" size="small" @click="handleSave">Save</el-button>
          <el-button size="small" @click="handleCancel">Cancel</el-button>
        </div>
      </div>
    </div>

    <div class="profile-section">
      <div class="profile-header">
        <h2>Club Type</h2>
        <el-button 
          v-if="canEdit && !isEditingType"
          type="primary" 
          size="small"
          @click="handleEditType"
        >
          Edit
        </el-button>
      </div>
      
      <div class="profile-content">
        <template v-if="!isEditingType">
          <div v-if="parsedClubTypes.length > 0" class="club-types-display">
            <span v-for="(type, index) in parsedClubTypes" :key="index" class="club-type-tag">
              {{ type }}
            </span>
          </div>
          <p v-else class="no-content">No type available</p>
        </template>
        <el-select
          v-else
          v-model="editedInfo.type"
          placeholder="Select club type"
        >
          <el-option
            v-for="type in club_type_list"
            :key="type"
            :label="type"
            :value="type"
          />
        </el-select>

        <div v-if="isEditingType" class="action-buttons">
          <el-button type="primary" size="small" @click="handleSaveType">Save</el-button>
          <el-button size="small" @click="handleCancelType">Cancel</el-button>
        </div>
      </div>
    </div>

    <div class="profile-section">
      <div class="profile-header">
        <h2>Current Leadership</h2>
      </div>

      <div class="profile-content">
        <div v-if="currentLeadership.length > 0" class="leadership-list">
          <div v-for="(position, index) in currentLeadership" :key="index" class="leadership-item">
            <span class="position-name">{{ position.position }}:</span>
            <span class="member-name">{{ getLeadershipMemberName(position) }}</span>
          </div>
        </div>
        <p v-else class="no-content">No leadership positions defined</p>
      </div>
    </div>

    <div class="profile-section">
      <div class="profile-header">
        <h2>Current Advisers</h2>
      </div>

      <div class="profile-content">
        <div v-if="currentAdvisers.length > 0" class="advisers-list">
          <div v-for="(adviser, index) in currentAdvisers" :key="index" class="adviser-item">
            <span class="adviser-name">{{ getAdviserFullName(adviser) }}</span>
            <span v-if="adviser.contact_email" class="adviser-email">({{ adviser.contact_email }})</span>
          </div>
        </div>
        <p v-else class="no-content">No advisers assigned</p>
      </div>
    </div>

    <div class="profile-section">
      <div class="profile-header">
        <h2>Created on</h2>
      </div>

      <div class="profile-content">
        <p>{{ formatCreationDate(clubInfo.create_time*1000  ) || 'Not available' }}</p>
      </div>
    </div>
  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { getUserType } from '@/utils/store'
import { postRequest } from '@/api/eclub'
import * as qiniu from "qiniu-js";
export default {
  name: 'ClubProfile',
  components: {
  },
  props: {
    clubInfo: {
      type: Object,
      default: () => ({}),
    },
    club_id: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      tempAvatarUrl:'',
      avatarFile:undefined,
      isEditing: false,
      isEditingName: false,
      isEditingType: false,
      editedInfo: { ...this.clubInfo },
      clubTypes: [
        { value: 'Leadership', label: 'Leadership' },
        { value: 'Academic and STEM', label: 'Academic and STEM' },
        { value: 'Arts and Culture', label: 'Arts and Culture' },
        { value: 'Interest and Hobby', label: 'Interest and Hobby' },
        { value: 'Vocation and Career', label: 'Vocation and Career' },
        { value: 'Environmental and Social', label: 'Environmental and Social' },
        { value: 'Sports and Physical Activities', label: 'Sports and Physical Activities' },
        { value: 'Others', label: 'Others' }
      ],
      club_type_list: [
        'Academic',
        'Arts',
        'Business & Finance',
        'Community Service & Volunteering', 
        'Culture & Diversity',
        'Environmental & Sustainability',
        'Health & Wellness',
        'Leadership',
        'Literature & Language',
        'Media & Journalism',
        'Music & Dance & Performing Arts',
        
        'Social Science',
        'Sports',
        'STEM',
        'Student Governance',
        'Technology & Engineering',
        'Vocation and Career',
        'Others',
      ],
      isEditingAvatar: false,
      uploadUrl: '/api/upload'
    }
  },

  /* 一.生命周期函数 */
  created () {
  },

  /* 二.监控函数 */
  watch: {
  },

  computed: {
    canEdit() {
      console.log(getUserType())
      return ['schoolAdmin', 'adviser'].includes(getUserType())
    },
    parsedClubTypes() {
      if (!this.clubInfo.type) return []

      // If it's already an array, return it
      if (Array.isArray(this.clubInfo.type)) {
        return this.clubInfo.type
      }

      // If it's a string that looks like JSON array, parse it
      if (typeof this.clubInfo.type === 'string') {
        const trimmed = this.clubInfo.type.trim()
        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed)
            return Array.isArray(parsed) ? parsed : [this.clubInfo.type]
          } catch (e) {
            return [this.clubInfo.type]
          }
        }
        // Return as single item array
        return [this.clubInfo.type]
      }

      return []
    },
    currentLeadership() {
      return this.clubInfo.leadership || []
    },
    currentAdvisers() {
      return this.clubInfo.advisors || []
    }
  },

  /* 三.内部功能函数 */
  methods: {

    clubAvatarStyle(){
      let stlye = {
        width: '100px',
        height: '100px',
        fontSize: '36px'
      }
      
      // Add white background and gray border when avatar exists
      if (this.clubInfo.avatar) {
        stlye.backgroundColor = '#ffffff'
        stlye.border = '1px solid #dcdfe6'
      }
      
      return stlye
    },

    /* ----------------------事件调用函数------------------------ */
    // 事件调用函数注释
    actionXxx () {

    },

    /* ----------------------内部功能函数------------------------ */
    // 内部功能函数注释
    doSomething () {

    },

    /* ----------------------服务请求函数------------------------ */
    // 服务请求函数注释
    ajaxXxx () {

    },

    handleEdit() {
      this.isEditing = true
      this.editedInfo = { ...this.clubInfo }
    },

    handleCancel() {
      this.isEditing = false
      this.editedInfo = { ...this.clubInfo }
    },

    async handleSave() {
      try {
        const dataToUpdate = {
          ...this.editedInfo,
          id: this.club_id
        }
        const response = await postRequest('updateClub', dataToUpdate)
        
        if (response.data.code === 0) {
          this.clubInfo = response.data.data
          this.isEditing = false
          this.$message.success('Club introduction updated successfully')
          this.$emit('club-updated')
        } else {
          this.$message.error(response.data.message || 'Failed to update club introduction')
        }
      } catch (error) {
        this.$message.error('Failed to update club introduction')
        console.error(error)
      }
    },

    handleEditName() {
      this.isEditingName = true
      this.editedInfo = { ...this.clubInfo }
    },

    handleCancelName() {
      this.isEditingName = false
      this.editedInfo = { ...this.clubInfo }
    },

    async handleSaveName() {
      try {
        const dataToUpdate = {
          ...this.editedInfo,
          id: this.club_id
        }
        const response = await postRequest('updateClub', dataToUpdate)
        
        if (response.data.code === 0) {
          this.clubInfo = response.data.data
          this.isEditingName = false
          this.$message.success('Club name updated successfully')
          this.$emit('club-updated')
        } else {
          this.$message.error(response.data.message || 'Failed to update club name')
        }
      } catch (error) {
        this.$message.error('Failed to update club name')
        console.error(error)
      }
    },

    handleEditType() {
      this.isEditingType = true
      this.editedInfo = { ...this.clubInfo }
    },

    handleCancelType() {
      this.isEditingType = false
      this.editedInfo = { ...this.clubInfo }
    },

    async handleSaveType() {
      try {
        const dataToUpdate = {
          ...this.editedInfo,
          id: this.club_id
        }
        const response = await postRequest('updateClub', dataToUpdate)
        
        if (response.data.code === 0) {
          this.clubInfo = response.data.data
          this.isEditingType = false
          this.$message.success('Club type updated successfully')
          this.$emit('club-updated')
        } else {
          this.$message.error(response.data.message || 'Failed to update club type')
        }
      } catch (error) {
        this.$message.error('Failed to update club type')
        console.error(error)
      }
    },

    handleEditAvatar() {
      this.isEditingAvatar = true;
      this.editedInfo = { ...this.clubInfo };
    },

    handleCancelAvatar() {
      this.isEditingAvatar = false
      this.editedInfo = { ...this.clubInfo }
    },

    async handleSaveAvatar() {
      try {
        const tokenResult =  await this.getQiniuToken()
        if(tokenResult.data.code != 0){
            this.$message.warning('Token error~')
            return
        }
        let that = this
        const token = tokenResult.data.data
        const file = this.avatarFile
        const observable = qiniu.upload(file,new Date().getTime() + file.name,token);
        const observer = {
          complete: (res) => {
            let url = "http://mainbucket.reachable-edu.com/" + res.key;
            that.pushAvatar(url)
          },
        };
        observable.subscribe(observer);
      } catch (error) {
        this.$message.error('Failed to update club avatar')
        console.error(error)
      }
    },
    async pushAvatar(avatar_url){
      this.editedInfo.avatar = avatar_url
      const dataToUpdate = {
          ...this.editedInfo,
          id: this.club_id
        }
      const response = await postRequest('updateClub', dataToUpdate)
        if (response.data.code === 0) {
          this.clubInfo = response.data.data
          this.isEditingAvatar = false
          this.$message.success('Club avatar updated successfully')
          this.$emit('club-updated')
        } else {
          this.$message.error(response.data.message || 'Failed to update club avatar')
        }
    },

    handleAvatarSuccess(res, file) {
      // if (res.code === 0) {
      //   this.editedInfo.avatar = res.data.url
      // } else {
      //   this.$message.error('Failed to upload avatar')
      // }
    },

    beforeAvatarUpload(file) {

      this.avatarFile = undefined
      this.tempAvatarUrl = ''

      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.error('Avatar must be JPG or PNG format!')
      }
      if (!isLt2M) {
        this.$message.error('Avatar size can not exceed 2MB!')
      }
      const result = isJPG && isLt2M
      if(result == true){
        this.avatarFile = file
        this.tempAvatarUrl = URL.createObjectURL(file)
      }
      return result
    },
    async getQiniuToken() {
      return new Promise((resolve) => {
        const response =  postRequest('getQiniuToken', {})
        resolve(response);
      });
    },

    formatCreationDate(timestamp) {
      if (!timestamp) return null;
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    getLeadershipMemberName(position) {
      if (!position.user) return 'Not assigned'
      return `${position.user.student_givenName_pinyin || ''} ${position.user.student_lastName_pinyin || ''}`.trim() || 'Not assigned'
    },

    getAdviserFullName(adviser) {
      if (!adviser) return 'N/A'
      const firstName = adviser.contact_givenName || ''
      const lastName = adviser.contact_lastName || ''
      return `${firstName} ${lastName}`.trim() || 'N/A'
    }
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.club-profile {
  padding: 20px;
  background: #fff;
  border-radius: 4px;

  .profile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
    }
  }

  .profile-content {
    line-height: 1.6;
    
    .no-content {
      color: #999;
      font-style: italic;
    }

    .club-avatar {
      display: block;
      margin: 0 auto;
      font-size: 36px !important;
    }
  }

  .action-buttons {
    margin-top: 16px;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .profile-section {
    margin-bottom: 30px;
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 30px;

    &:last-child {
      margin-bottom: 0;
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .avatar-uploader {
    text-align: center;
    
    .avatar-preview {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      object-fit: cover;
    }

    .avatar-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 100px;
      height: 100px;
      line-height: 100px;
      text-align: center;
      border: 1px dashed #d9d9d9;
      border-radius: 50%;
    }

    &:hover {
      .avatar-uploader-icon {
        border-color: #409EFF;
      }
    }
  }

  .avatar-edit-button {
    margin-top: 16px;
    display: block;
  }
  
  .club-avatar {
    font-size: 36px !important;
  }

  .club-types-display {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .club-type-tag {
      display: inline-block;
      font-size: 13px;
      color: #666;
      background: #f5f7fa;
      padding: 6px 14px;
      border-radius: 14px;
      border: 1px solid #e4e7ed;
      white-space: nowrap;
    }
  }

  .school-name-display {
    font-size: 14px;
    color: #999;
    font-weight: normal;
    font-style: italic;
    margin-left: 8px;
  }

  .leadership-list,
  .advisers-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .leadership-item,
  .adviser-item {
    display: flex;
    align-items: center;
    padding: 10px;
    background: #f5f7fa;
    border-radius: 4px;
    gap: 8px;

    .position-name {
      font-weight: 600;
      color: #303133;
    }

    .member-name {
      color: #606266;
    }

    .adviser-name {
      font-weight: 500;
      color: #303133;
    }

    .adviser-email {
      color: #909399;
      font-size: 13px;
    }
  }
}
</style>
